package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.domain.AccessState
import com.rosario.juridico.navigation.Routes
import juridico5.composeapp.generated.resources.Res
import org.jetbrains.compose.resources.ExperimentalResourceApi
import kotlinx.coroutines.launch
import kotlinx.coroutines.yield
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.collectAsState

private data class DocumentoDisponible(
    val titulo: String,
    val resId: String,
    val descripcion: String,
)

private data class EscritoProcesal(
    val titulo: String,
    val uso: String,
    val baseLegal: String,
    val modelo: String,
)

private val documentosBase = listOf(
    DocumentoDisponible(
        titulo = "Mediacion: como funciona",
        resId = "consejos_mediacion.txt",
        descripcion = "Guia practica para encarar una mediacion prejudicial obligatoria.",
    ),
    DocumentoDisponible(
        titulo = "Acuerdo alimentos definitivos",
        resId = "acuerdo_alimentos_definitivos.txt",
        descripcion = "Modelo base para acuerdos alimentarios.",
    ),
    DocumentoDisponible(
        titulo = "Escritos para mediadores prejudicial obligatoria",
        resId = "modelos_escritos_para_abogados_mediadores_prejudicial_obligatoria.txt",
        descripcion = "Modelos y apoyos para mediacion prejudicial.",
    ),
    DocumentoDisponible(
        titulo = "Poder general",
        resId = "poder_general.txt",
        descripcion = "Texto base para poder general.",
    ),
)

@Composable
fun PantallaDocumentos(navController: NavController) {
    var busqueda by remember { mutableStateOf("") }
    var escritosProcesales by remember { mutableStateOf<List<EscritoProcesal>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }

    // --- MOTOR DE USOS (FREEMIUM) ---
    val userStatus by AppUsageManager.instance.userStatus.collectAsState()
    val accessState = AppUsageManager.instance.checkAccess()
    val isPremium = accessState == AccessState.PREMIUM_ACTIVE
    
    val quota = com.rosario.juridico.platform.UserQuota(
        initial_uses_remaining = userStatus.initial_uses_remaining,
        last_monthly_use_timestamp = userStatus.last_monthly_use_timestamp
    )
    val accessResult = com.rosario.juridico.platform.checkAccess(quota)
    val isLocked = !isPremium && accessResult == com.rosario.juridico.platform.AccessResult.DENIED
    // --- FIN MOTOR ---

    LaunchedEffect(Unit) {
        isLoading = true
        escritosProcesales = cargarEscritosProcesales()
        isLoading = false
    }

    val escritosFiltrados = remember(busqueda, escritosProcesales) {
        filtrarEscritos(busqueda, escritosProcesales)
    }

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.background)
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            item {
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text(
                        text = "Modelos y documentos",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary,
                    )
                    Text(
                        text = "Accede a modelos de locacion y a una base local de escritos procesales ya migrados al proyecto multiplataforma.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            item {
                SeccionDocumentosLabel("Contratos y documentos")
            }

            item {
                CalculatorActionLock(
                    navController = navController,
                    onAction = { navController.navigate(Routes.MODELOS_ALQUILER) }
                ) { onClick ->
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                        onClick = onClick,
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(42.dp)
                                    .background(MaterialTheme.colorScheme.primaryContainer, RoundedCornerShape(12.dp)),
                                contentAlignment = Alignment.Center,
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Folder,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.primary,
                                )
                            }
                            Column(
                                modifier = Modifier
                                    .weight(1f)
                                    .padding(start = 12.dp),
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(
                                        text = "Modelos escritos inmobiliarios",
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.Bold,
                                    )
                                    if (isLocked) {
                                        Spacer(Modifier.width(8.dp))
                                        Icon(Icons.Default.Lock, null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(16.dp))
                                    }
                                }
                                Text(
                                    text = "Vivienda, comercial, temporario y documentos asociados.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                )
                            }
                        }
                    }
                }
            }

            items(documentosBase) { documento ->
                CalculatorActionLock(
                    navController = navController,
                    onAction = {
                        navController.navigate(Routes.visorPdf(documento.titulo, documento.resId))
                    }
                ) { onClick ->
                    DocumentoCard(
                        documento = documento,
                        isLocked = isLocked,
                        onClick = onClick,
                    )
                }
            }

            item {
                Spacer(modifier = Modifier.height(4.dp))
                SeccionDocumentosLabel("Escritos procesales")
            }

            item {
                OutlinedTextField(
                    value = busqueda,
                    onValueChange = { busqueda = it },
                    label = { Text("Buscar escrito") },
                    placeholder = { Text("Ej: apelacion, excepciones, prueba") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
                    trailingIcon = {
                        if (busqueda.isNotBlank()) {
                            IconButton(onClick = { busqueda = "" }) {
                                Icon(Icons.Default.Close, contentDescription = "Limpiar")
                            }
                        }
                    },
                )
            }

            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    listOf("Apelacion", "Excepciones", "Prueba", "Sentencia").forEach { sugerencia ->
                        AssistChip(
                            onClick = { busqueda = sugerencia },
                            label = { Text(sugerencia) },
                        )
                    }
                }
            }

            if (busqueda.isNotBlank()) {
                item {
                    Text(
                        text = "${escritosFiltrados.size} resultado(s) para \"$busqueda\"",
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            if (isLoading) {
                item {
                    Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                }
            } else if (escritosFiltrados.isEmpty() && busqueda.isNotBlank()) {
                item {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                    ) {
                        Row(
                            modifier = Modifier.padding(14.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Icon(
                                imageVector = Icons.Default.SearchOff,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                            Text(
                                text = "No se encontraron escritos para esa busqueda.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(start = 8.dp),
                            )
                        }
                    }
                }
            } else {
                items(escritosFiltrados) { escrito ->
                    CalculatorActionLock(
                        navController = navController,
                        onAction = {
                            navController.navigate(
                                Routes.visorPdf(
                                    titulo = escrito.titulo,
                                    resId = "escritos_procesales.txt"
                                )
                            )
                        }
                    ) { onClick ->
                        EscritoProcesalCard(
                            escrito = escrito,
                            isLocked = isLocked,
                            onClick = onClick,
                        )
                    }
                }
            }

            item {
                Spacer(modifier = Modifier.height(24.dp))
            }
        }
    }
}

@OptIn(ExperimentalResourceApi::class)
@Composable
fun PantallaContenidoDocumento(titulo: String, resId: String) {
    var contenido by remember(resId) { mutableStateOf<String?>(null) }
    var error by remember(resId) { mutableStateOf<String?>(null) }

    LaunchedEffect(resId) {
        contenido = null
        error = null
        try {
            contenido = cargarContenidoDocumento(titulo, resId)
        } catch (e: Exception) {
            error = "No se pudo cargar el documento."
        }
    }

    val scrollState = rememberScrollState()

    WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.background)
                .verticalScroll(scrollState)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = titulo,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary,
                )
                Text(
                    text = resId,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (contenido != null) {
                IconButton(onClick = { copiarAlPortapapeles(titulo, contenido.orEmpty()) }) {
                    Icon(Icons.Default.ContentCopy, contentDescription = "Copiar")
                }
            }
        }

        when {
            error != null -> {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer),
                ) {
                    Text(
                        text = error.orEmpty(),
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.padding(14.dp),
                    )
                }
            }

            contenido == null -> {
                Card(modifier = Modifier.fillMaxWidth()) {
                    Text(
                        text = "Cargando documento...",
                        modifier = Modifier.padding(14.dp),
                    )
                }
            }

            else -> {
                val textoCopia = """
                    ${titulo.uppercase()}
                    -------------------------------------------
                    $contenido
                    -------------------------------------------
                    Obtenido con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "Vista previa del documento",
                    textoParaCompartir = textoCopia,
                    colorFondo = MaterialTheme.colorScheme.surface
                ) {
                    TextoInteractivoAI(contenido.orEmpty())
                }
            }
        }

        Spacer(modifier = Modifier.height(40.dp))
    }
}
}

@Composable
private fun SeccionDocumentosLabel(texto: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Box(
            modifier = Modifier
                .size(width = 4.dp, height = 18.dp)
                .background(MaterialTheme.colorScheme.secondary, RoundedCornerShape(2.dp)),
        )
        Text(
            text = texto,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.padding(start = 8.dp),
        )
    }
}

@Composable
private fun DocumentoCard(
    documento: DocumentoDisponible,
    isLocked: Boolean = false,
    onClick: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = CardDefaults.outlinedCardBorder(),
        onClick = onClick,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = if (isLocked) Icons.Default.Lock else Icons.Default.Description,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
            Column(
                modifier = Modifier
                    .weight(1f)
                    .padding(start = 12.dp),
            ) {
                Text(
                    text = documento.titulo,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = documento.descripcion,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun EscritoProcesalCard(
    escrito: EscritoProcesal,
    isLocked: Boolean = false,
    onClick: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = CardDefaults.outlinedCardBorder(),
        onClick = onClick,
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = if (isLocked) Icons.Default.Lock else Icons.Default.Article,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                )
                Text(
                    text = escrito.titulo,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(start = 10.dp),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (escrito.uso.isNotBlank()) {
                Text(
                    text = escrito.uso,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 8.dp),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (escrito.baseLegal.isNotBlank()) {
                Spacer(modifier = Modifier.height(8.dp))
                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.15f))
                Text(
                    text = escrito.baseLegal,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }
        }
    }
}

@OptIn(ExperimentalResourceApi::class)
private suspend fun cargarEscritosProcesales(): List<EscritoProcesal> = withContext(Dispatchers.Default) {
    try {
        val bytes = Res.readBytes("files/escritos_procesales.txt")
        yield()
        val texto = bytes.decodeToString()
        yield()
        parsearEscritosProcesales(texto)
    } catch (e: Exception) {
        emptyList()
    }
}

@OptIn(ExperimentalResourceApi::class)
private suspend fun cargarContenidoDocumento(
    titulo: String,
    resId: String,
): String = withContext(Dispatchers.Default) {
    try {
        val bytes = Res.readBytes("files/$resId")
        yield()
        val texto = bytes.decodeToString()
        yield()
        if (resId != "escritos_procesales.txt") return@withContext texto

        val escrito = parsearEscritosProcesales(texto).firstOrNull { it.titulo == titulo }
        if (escrito != null) {
            buildString {
                appendLine(escrito.titulo)
                if (escrito.uso.isNotBlank()) appendLine("USO: ${escrito.uso}")
                if (escrito.baseLegal.isNotBlank()) appendLine("BASE LEGAL: ${escrito.baseLegal}")
                appendLine()
                append(escrito.modelo)
            }
        } else {
            texto
        }
    } catch (e: Exception) {
        ""
    }
}

private suspend fun parsearEscritosProcesales(texto: String): List<EscritoProcesal> {
    val bloquesRaw = texto.split("--------------------")
    yield()
    
    val resultados = mutableListOf<EscritoProcesal>()
    
    bloquesRaw.chunked(10).forEach { chunk ->
        chunk.forEach { bloque ->
            val cleanBloque = bloque.trim()
            if (cleanBloque.isNotBlank()) {
                val lineas = cleanBloque.lines()
                val titulo = lineas
                    .firstOrNull { it.startsWith("===") }
                    ?.replace("===", "")
                    ?.trim()

                if (titulo != null) {
                    val uso = lineas
                        .firstOrNull { it.startsWith("USO:") }
                        ?.removePrefix("USO:")
                        ?.trim()
                        .orEmpty()

                    val baseLegal = lineas
                        .firstOrNull { it.startsWith("BASE LEGAL:") }
                        ?.removePrefix("BASE LEGAL:")
                        ?.trim()
                        .orEmpty()

                    val modeloIndex = lineas.indexOfFirst { it.startsWith("MODELO:") }
                    val modelo = if (modeloIndex >= 0) {
                        lineas.drop(modeloIndex + 1).joinToString("\n").trim()
                    } else {
                        ""
                    }

                    resultados.add(
                        EscritoProcesal(
                            titulo = titulo,
                            uso = uso,
                            baseLegal = baseLegal,
                            modelo = modelo,
                        )
                    )
                }
            }
        }
        yield() 
    }
    
    return resultados
}

private fun filtrarEscritos(
    query: String,
    escritos: List<EscritoProcesal>,
): List<EscritoProcesal> {
    if (query.isBlank()) return escritos

    val terminos = query
        .lowercase()
        .split(" ", ",", ".")
        .filter { it.length > 2 }

    if (terminos.isEmpty()) return escritos

    return escritos.filter { escrito ->
        val haystack = "${escrito.titulo} ${escrito.uso} ${escrito.baseLegal}".lowercase()
        terminos.any { termino -> haystack.contains(termino) }
    }
}
